import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { db } from '../firebase/firebase-config';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { Package, ChevronLeft, Star, ArrowRight, Activity, Truck, Scale, Zap } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from '../Navbar/Navbar';
import Footer from '../Footer/Footer';
import './CategoryDetail.css';
import Loader from '../Loader/Loader';
import { useLanguage } from '../../context/LanguageContext';

const CategoryDetail = () => {
  const { categorySlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { lang, t } = useLanguage();

  const [catInfo, setCatInfo] = useState({
    id: location.state?.catId || '',
    title: location.state?.catTitle || '',
    titleEn: location.state?.catTitleEn || ''
  });

  const [products, setProducts] = useState([]);
  const [heroImage, setHeroImage] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');

  // KANKA: Ürün isimleri için Arapça destekli "On-the-Fly" çeviri sözlüğü
  const getTranslatedName = (name) => {
    if (lang === 'TR') return name;
    
    const nameMap = {
      'Koyun Süt Yemi': lang === 'AR' ? 'علف حليب الأغنام' : 'Sheep Milk Feed',
      'Sığır Besi Yemi': lang === 'AR' ? 'علف تسمين الماشية' : 'Cattle Beef Feed',
      'Kuzu Başlangıç Yemi': lang === 'AR' ? 'علف بداية الحملان' : 'Lamb Starter Feed',
      'Buzağı Büyütme Yemi': lang === 'AR' ? 'علف نمو العجول' : 'Calf Growth Feed'
    };

    return nameMap[name] || name; 
  };

  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      let currentId = catInfo.id;
      try {
        const sliderSnap = await getDocs(query(collection(db, "sliders"), orderBy("createdAt", "desc"), limit(1)));
        if (!sliderSnap.empty) setHeroImage(sliderSnap.docs[0].data().url);

        if (!currentId) {
          const catSnap = await getDocs(collection(db, "categories"));
          const allCats = catSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          const matchedCat = allCats.find(c => 
            c.title.toLowerCase().replace(/ /g, '-').replace(/[^\w-]+/g, '') === categorySlug
          );
          if (matchedCat) {
            currentId = matchedCat.id;
            setCatInfo({ 
                id: matchedCat.id, 
                title: matchedCat.title,
                titleEn: matchedCat.titleEn,
                titleAr: matchedCat.titleAr // Kanka Arapça başlığı da aldık
            });
          }
        }

        if (currentId) {
          const q = query(collection(db, "products"), where("categoryId", "==", currentId));
          const prodSnap = await getDocs(q);
          setProducts(prodSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        }
      } catch (err) { console.error("Hata:", err); }
      setLoading(false);
    };
    initializeData();
  }, [categorySlug, catInfo.id]);

  const filteredProducts = activeFilter === 'all' 
    ? products 
    : products.filter(p => p.subType === activeFilter);

  const isLivestock = catInfo.title?.toLowerCase().includes('büyükbaş') || catInfo.title?.toLowerCase().includes('küçükbaş');

  if (loading) return <Loader text={lang === 'TR' ? "Bigma Üretim Verileri Yükleniyor..." : lang === 'AR' ? "جاري تحميل بيانات إنتاج بيغما..." : "Loading Bigma Production Data..."} />;

  return (
    <div className="subpage-wrapper">
      <Navbar />

      <section className="category-detail-hero" style={{ backgroundImage: `url(${heroImage || '/default-hero.jpg'})` }}>
        <div className="hero-overlay-premium">
          <div className="container">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="hero-content-center">
              <div className="category-badge">{lang === 'TR' ? "Bigma Üretim Grubu" : lang === 'AR' ? "مجموعة إنتاج بيغما" : "Bigma Production Group"}</div>
              <h1>{lang === 'TR' ? catInfo.title : lang === 'AR' ? (catInfo.titleAr || catInfo.titleEn || catInfo.title) : (catInfo.titleEn || catInfo.title)}</h1>
              <p>
                {lang === 'TR' 
                    ? "Profesyonel rasyonlarla hazırlanan yüksek verimli çözümler." 
                    : lang === 'AR'
                    ? "حلول عالية الكفاءة معدة بتركيبات غذائية احترافية."
                    : "High-efficiency solutions prepared with professional rations."}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <div className="navigation-section-modern">
        <div className="container">
          <button className="back-link-new" onClick={() => navigate('/urunlerimiz')}>
            <ChevronLeft size={18} /> {lang === 'TR' ? "Tüm Üretim Grupları" : lang === 'AR' ? "جميع مجموعات الإنتاج" : "All Production Groups"}
          </button>
        </div>
      </div>

      {isLivestock && (
        <div className="filter-wrapper-outer">
          <div className="filter-track-premium">
            <div className={`filter-node ${activeFilter === 'all' ? 'active' : ''}`} onClick={() => setActiveFilter('all')}>
              <div className="icon-circle"><Package size={22} /></div>
              <span>{lang === 'TR' ? "Tümü" : lang === 'AR' ? "الكل" : "All"}</span>
            </div>
            <div className={`filter-node ${activeFilter === 'besi' ? 'active' : ''}`} onClick={() => setActiveFilter('besi')}>
              <div className="icon-circle"><Activity size={22} /></div>
              <span>{lang === 'TR' ? "Besi Yemleri" : lang === 'AR' ? "أعلاف التسمين" : "Beef Feeds"}</span>
            </div>
            <div className={`filter-node ${activeFilter === 'sut' ? 'active' : ''}`} onClick={() => setActiveFilter('sut')}>
              <div className="icon-circle"><Truck size={22} /></div>
              <span>{lang === 'TR' ? "Süt Yemleri" : lang === 'AR' ? "أعلاف الحليب" : "Dairy Feeds"}</span>
            </div>
          </div>
        </div>
      )}

      <div className="container product-grid-container">
        <AnimatePresence mode="wait">
          {filteredProducts.length > 0 ? (
            <motion.div key="grid" className="product-list-grid" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              {filteredProducts.map((prod, index) => (
                <motion.div 
                  key={prod.id} 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="product-premium-card"
                  onClick={() => navigate(`/urun/${prod.id}`, { state: { product: prod } })}
                >
                  {prod.isBestSeller && (
                    <div className="best-seller-tag">
                      <Star size={12} fill="white" /> {lang === 'TR' ? "Çok Satan" : lang === 'AR' ? "الأكثر مبيعاً" : "Best Seller"}
                    </div>
                  )}
                  
                  <div className="prod-img-box">
                    <img src={prod.imageUrl} alt={lang === 'TR' ? prod.name : getTranslatedName(prod.name)} />
                  </div>

                  <div className="prod-content">
                    <span className="prod-category-label">
                        {lang === 'TR' 
                          ? (prod.subType?.toUpperCase() || 'GENEL') 
                          : lang === 'AR'
                          ? (prod.subType === 'besi' ? 'تسمين' : prod.subType === 'sut' ? 'حليب' : 'عام')
                          : (prod.subType === 'besi' ? 'BEEF' : prod.subType === 'sut' ? 'DAIRY' : 'GENERAL')}
                    </span>
                    <h3>{getTranslatedName(prod.name)}</h3>
                    
                    <div className="prod-tech-tags">
                      <div className="tech-tag">
                         <Scale size={14} /> {prod.weight} {lang === 'AR' ? 'كجم' : 'KG'}
                      </div>
                      <div className="tech-tag">
                        <Zap size={14} /> 
                        {lang === 'TR' 
                          ? (prod.type || 'Pelet') 
                          : lang === 'AR'
                          ? (prod.type === 'Pelet' ? 'مكبوس' : 'مكبوس')
                          : (prod.type === 'Pelet' ? 'Pellet' : (prod.type || 'Pellet'))}
                      </div>
                    </div>

                    <button className="view-analyze-btn">
                      {lang === 'TR' ? "İncele" : lang === 'AR' ? "عرض" : "Examine"} <ArrowRight size={18} />
                    </button>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <div className="no-products-info">
                {lang === 'TR' ? "Bu grupta henüz eklenmiş ürün bulunmuyor." : lang === 'AR' ? "لا توجد منتجات مضافة إلى هذه المجموعة بعد." : "There are no products added to this group yet."}
            </div>
          )}
        </AnimatePresence>
      </div>

      <Footer />
    </div>
  );
};

export default CategoryDetail;